import React from 'react'
import { styled } from 'styled-components'
import { CreativeCanvasDocument } from './CreativeCanvas'

/**
 * The components in this file are meant to be used together to display a  list of teams and a corresponding graphic, often that team's canvas.
 * They are meant to be used like so:
 *
 *       <MultiCanvasDisplay>
 *           {teams.map(team => (
 *             <TeamCanvasDisplay>
 *               <TeamHeader team={team} />
 *               <CanvasContainer />
 *             </TeamCanvasDisplay>
 *           ))}
 *       </MultiCanvasDisplay>
 *
 */

/**
 * A container to display multiple canvases. Organizes the children into a grid based on the number of
 * children.
 */
export function MultiCanvasDisplay({
	children,
	...props
}: {
	children: React$Node[],
}): React$Node {
	const { cols, rows } = getLayout(children.length)

	return (
		<MultiCanvasDisplayStyle
			{...{
				...props,
				$cols: cols,
				$rows: rows,
			}}>
			{children}
		</MultiCanvasDisplayStyle>
	)
}

/**
 * A container for a single team's header and canvas. Displays the team's icon and name at the top, and the canvas
 */
export function TeamCanvasDisplay(props: {}): React$Node {
	return <TeamCanvasPositioner {...props} />
}

/**
 * A tag that displays the given teams icon and name.
 */
export function TeamHeader({
	team,
	className,
}: {
	team: { icon: string, displayName: string },
	className?: string,
}): React$Node {
	return (
		<TeamHeaderStyled className={className}>
			<img src={team.icon} />
			<h1 className="font-game-headline">{team.displayName}</h1>
		</TeamHeaderStyled>
	)
}

/**
 * A small container around `CreativeCanvasDocument` that makes it easier to use in a `TeamCanvasDisplay`
 */
export function CanvasContainer({
	canvasId,
	documentId,
	...props
}: {
	canvasId: string,
	documentId: string,
}): React$Node {
	return (
		<CreativeCanvasDocumentPositioner {...props}>
			<CreativeCanvasDocument canvasId={canvasId} documentId={documentId} />
		</CreativeCanvasDocumentPositioner>
	)
}

/**
 * Returns the column/row count for the given number of items.
 */
export function getLayout(itemCount: number): { cols: number, rows: number } {
	// 1, 2, and 9 are special cases
	if (itemCount === 1) {
		return {
			cols: 1,
			rows: 1,
		}
	} else if (itemCount < 3) {
		return {
			cols: itemCount,
			rows: 1,
		}
	} else if (itemCount === 2) {
		return {
			cols: 2,
			rows: 1,
		}
	} else if (itemCount === 9) {
		return {
			cols: 3,
			rows: 3,
		}
	}

	// never have more than 4 columns or less than 2 rows
	return {
		cols: Math.min(4, Math.ceil(itemCount / 2)),
		rows: Math.max(2, Math.ceil(itemCount / 4)),
	}
}

/**
 * The wrapper for the display results step of the collaborative culminating moment screen. Uses `$cols`
 * and `$rows` to determine the number of columns and rows to display.
 * @param props
 * @param props.$cols The number of columns to display. This is the max number of entries in any row.
 * @param props.$rows How many rows of items there should be.
 */

const MultiCanvasDisplayStyle = styled.div`
	${({ $cols, $rows }) => `
        --columns: ${$cols};
        --rows: ${$rows};
        --gap-size: ${
					$rows > 2 || $cols > 2 ? `var(--spacing-2x)` : `var(--spacing-4x)`
				};
    `}

	// computed values
	--column-gap-count: calc(var(--columns) - 1);
	--row-gap-count: calc(var(--rows) - 1);
	--child-width: calc(
		(100% - (var(--column-gap-count) * var(--gap-size))) / var(--columns)
	);
	--child-height: calc(
		(100% - (var(--row-gap-count) * var(--gap-size))) / var(--rows)
	);

	--header-height: calc(2em + var(--spacing-2x));

	flex: 1;

	display: flex;
	flex-wrap: wrap;
	align-items: center;
	align-content: center;
	justify-content: center;
	gap: var(--gap-size);
`
const TeamHeaderStyled = styled.div`
	display: inline-flex;
	align-items: center;
	z-index: 2;
	border-radius: var(--spacing);
	padding: var(--spacing);
	letter-spacing: 2px;
	--font-size: 1em;
	--icon-size: 1.5em;

	img {
		margin-right: var(--spacing);
		max-height: var(--icon-size);
		height: var(--icon-size);
	}
	h1 {
		font-size: var(--font-size);
		margin: 0;
	}
`

const TeamCanvasPositioner = styled.div`
	z-index: 1;
	position: relative;
	height: 100%;
	width: 100%;
	max-width: var(--child-width);
	max-height: var(--child-height);
	margin: auto;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`

const CreativeCanvasDocumentPositioner = styled.div`
	aspect-ratio: 4 / 3;
	height: calc(100% - var(--header-height));
	max-width: 100%;
`
